import React, { FC, useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Chip,
  Link,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import { Post } from '@libs/models';
import { toHumanArray } from '@libs/utils';
import { useGetJobseekerRooftopQuery, useLazyGetJobseekerRooftopPostsQuery } from '@carcareers/store';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
export const Rooftop: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [posts, setPosts] = useState<Record<string, Post>>({});
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const { data: rooftopData, isLoading: isRooftopLoading } = useGetJobseekerRooftopQuery(Number(id), { skip: !id });
  const [fetchPosts, { isFetching }] = useLazyGetJobseekerRooftopPostsQuery();

  // Load initial posts
  useEffect(() => {
    if (id) {
      loadPosts(1);
    }
  }, [id]);

  const loadPosts = async (pageNum: number) => {
    if (!id || loading) return;

    setLoading(true);
    try {
      const response = await fetchPosts({ id: Number(id), page: pageNum, limit: 3 }).unwrap();

      const newPosts: Record<string, Post> = {};
      response.items.forEach((item) => {
        newPosts[`${item.id}-${item.job?.id}`] = item;
      });

      if (pageNum === 1) {
        setPosts(newPosts);
      } else {
        setPosts((prevPosts) => ({ ...prevPosts, ...newPosts }));
      }

      setHasMore(pageNum < (response.meta.totalPages || 0));
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadPosts(page + 1);
    }
  };

  if (isRooftopLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 136px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!rooftopData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 136px)' }}>
        <Typography variant="h6">Rooftop not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Rooftop Information Card */}
      <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2, mb: 4 }}>
        <Box
          sx={{
            p: 3,
            position: 'relative',
            overflow: 'hidden',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
              opacity: 0.1,
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h2" component="h1" gutterBottom>
              {rooftopData.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationOnIcon sx={{ mr: 1 }} />
              <Typography variant="h6">{rooftopData.location}</Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ p: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h4" gutterBottom>
              About Us
            </Typography>
            <Typography paragraph sx={{ whiteSpace: 'pre-line' }}>
              {rooftopData.description}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h4" gutterBottom>
              Contact Information
            </Typography>
            <List disablePadding>
              {rooftopData.phone && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Link href={`tel:${rooftopData.phone}`} underline="hover">
                        {rooftopData.phone}
                      </Link>
                    }
                  />
                </ListItem>
              )}

              {rooftopData.email && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Link href={`mailto:${rooftopData.email}`} underline="hover">
                        {rooftopData.email}
                      </Link>
                    }
                  />
                </ListItem>
              )}

              {rooftopData.website && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon>
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Link
                        href={
                          rooftopData.website.startsWith('http')
                            ? rooftopData.website
                            : `https://${rooftopData.website}`
                        }
                        underline="hover"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {rooftopData.website}
                      </Link>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Posts List Section */}
      <Paper elevation={3} sx={{ borderRadius: 2, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <WorkIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h3">Available Positions</Typography>
        </Box>

        {Object.keys(posts).length > 0 ? (
          <>
            <List>
              {Object.entries(posts).map(([key, post], index) => (
                <React.Fragment key={key}>
                  {index > 0 && <Divider sx={{ my: 2 }} />}
                  <ListItem
                    sx={{
                      display: 'block',
                      p: 2,
                      transition: 'background-color 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'grey.100',
                        borderRadius: 4,
                      },
                    }}
                    component="div"
                    disablePadding
                    onClick={() => {
                      navigate(`/jobs/${post.id}`);
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 600,
                        }}
                      >
                        {post.job?.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ArrowForwardIcon
                          sx={{
                            fontSize: '1rem',
                            color: 'primary.main',
                            transition: 'transform 0.2s ease-in-out',
                            '.MuiListItem-root:hover &': {
                              transform: 'translateX(4px)',
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 8 }}>
                          <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <AttachMoneyIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${post.job?.compensationMin} - ${post.job?.compensationMax} ${post.job?.compensationType}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <LocationOnIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={rooftopData.location}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CalendarTodayIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={`Posted: ${new Date(post.createdAt).toLocaleDateString()}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          </List>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          {post.job?.benefits && post.job.benefits.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {toHumanArray('camel', post.job.benefits, true).map((benefit, i) => (
                                <Chip
                                  key={`${key}-benefit-${i}`}
                                  size="small"
                                  label={benefit}
                                  sx={{
                                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                                    fontSize: '0.7rem',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loading || isFetching}
                  startIcon={loading || isFetching ? <CircularProgress size={20} /> : undefined}
                >
                  {loading || isFetching ? 'Loading...' : 'Load More Jobs'}
                </Button>
              </Box>
            )}
          </>
        ) : loading || isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No open positions at this time.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Rooftop;
